import { IAddress } from '@/models';
import { IAddressInfo } from '@/models/cfs';

type AddressDisplayValue = Pick<
  IAddress,
  | 'addressLabel'
  | 'addressNumber'
  | 'completeStreetName'
  | 'subAddress'
  | 'intersectionStreets'
  | 'zipName'
  | 'state'
  | 'zipcode'
>;

const normalizeAddressPart = (value?: string | null) => value?.trim() ?? '';

export const getAddressLabelText = (address?: AddressDisplayValue): string => {
  if (!address) {
    return '';
  }

  const addressNumber = normalizeAddressPart(address.addressNumber);
  const completeStreetName = normalizeAddressPart(address.completeStreetName);
  const addressLabel = normalizeAddressPart(address.addressLabel);

  if (completeStreetName) {
    return [addressNumber, completeStreetName].filter(Boolean).join(' ');
  }

  return addressLabel;
};

export const getNormalizedAddressText = (
  address?: AddressDisplayValue,
): string => {
  if (!address) {
    return '';
  }

  const street = getAddressLabelText(address);
  const zipName = normalizeAddressPart(address.zipName);
  const state = normalizeAddressPart(address.state);
  const zipcode = normalizeAddressPart(address.zipcode);

  const parts = [street, zipName].filter(Boolean);
  const stateZip = [state, zipcode].filter(Boolean).join(' ');

  if (stateZip) {
    parts.push(stateZip);
  }

  return parts.join(', ');
};

export const isValidStructuredAddress = (
  address?: Partial<AddressDisplayValue> | null,
): boolean => {
  if (!address) {
    return false;
  }

  const hasStreet =
    !!normalizeAddressPart(address.completeStreetName) ||
    !!normalizeAddressPart(address.addressLabel);
  const hasIntersection = !!address.intersectionStreets?.length;
  const hasLocation =
    !!normalizeAddressPart(address.zipName) ||
    !!normalizeAddressPart(address.state) ||
    !!normalizeAddressPart(address.zipcode);

  return (hasStreet || hasIntersection) && hasLocation;
};

export const isResolvableAddress = (
  address?: Partial<AddressDisplayValue> | null,
): boolean =>
  !!address?.zipName && !!address?.state && !!address?.completeStreetName;

export const getAddressLocation = (
  address?: AddressDisplayValue,
  ignoreSubAddress?: boolean,
): string => {
  if (!address) {
    return '';
  }
  const { subAddress, intersectionStreets } = address;

  const parts: string[] = [];

  let addressLine = getAddressLabelText(address);
  if (intersectionStreets && intersectionStreets.length) {
    addressLine = intersectionStreets.join(' & ');
  }
  if (!ignoreSubAddress && subAddress) {
    addressLine = `${addressLine} ${subAddress}`;
  }
  if (addressLine) parts.push(addressLine);

  const normalizedSuffix = getNormalizedAddressText(address)
    .split(', ')
    .slice(1)
    .filter(Boolean);
  parts.push(...normalizedSuffix);

  return parts.filter(Boolean).join(', ');
};

// String of same fields of Main address item and sub-address item.
export const getAddressCommonString = (address?: IAddress) => {
  if (!address) return '';
  return `${address.addressNumber}, ${address.completeStreetName}, ${address.zipcode}`;
};

// Object with lat and lng of address point.
export const getAddressCoordinates = (
  address?: IAddress,
): { lat: number; lng: number } | null => {
  if (!address || !address.point || !address.point.coordinates) return null;
  return {
    lat: address.point.coordinates[1],
    lng: address.point.coordinates[0],
  };
};

const getLocationPointLabel = (
  addressInfo?: IAddressInfo,
  fallbackToCoordinates = true,
) => {
  const locationPoint =
    addressInfo?.locationPoints?.find((lp) => lp.isPrimary) ??
    addressInfo?.locationPoints?.[0];
  if (!locationPoint?.point?.coordinates?.length) {
    return '';
  }

  if (locationPoint.name) {
    return locationPoint.name;
  }

  if (!fallbackToCoordinates) {
    return '';
  }

  const [lng, lat] = locationPoint.point.coordinates;
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

export const getBusinessNamePrefixedText = (
  businessName: string | null | undefined,
  location: string,
): string => {
  const name = businessName?.trim();
  return name && location ? `${name} - ${location}` : location;
};

export const getAddressInfoLocation = (
  addressInfo?: IAddressInfo,
  ignoreSubAddress?: boolean,
): string => {
  const addressText = getAddressLocation(
    addressInfo?.address,
    ignoreSubAddress,
  );
  if (addressText) {
    return getBusinessNamePrefixedText(
      addressInfo?.businessName ?? addressInfo?.address?.businessName,
      addressText,
    );
  }
  return getLocationPointLabel(addressInfo);
};

export const getAddressInfoCoordinates = (
  addressInfo?: IAddressInfo,
): { lat: number; lng: number } | null => {
  const primaryLocationPoint = addressInfo?.locationPoints?.find(
    (lp) => lp.isPrimary,
  )?.point?.coordinates;

  if (primaryLocationPoint && primaryLocationPoint.length === 2) {
    return {
      lat: primaryLocationPoint[1],
      lng: primaryLocationPoint[0],
    };
  }

  const addressCoordinates = getAddressCoordinates(addressInfo?.address);
  if (addressCoordinates) {
    return addressCoordinates;
  }

  const coordinates = addressInfo?.locationPoints?.[0]?.point?.coordinates;
  if (!coordinates || coordinates.length !== 2) {
    return null;
  }

  return {
    lat: coordinates[1],
    lng: coordinates[0],
  };
};

export const getAddressPrePlan = (address?: {
  addressPrePlans?: any[] | null;
}) => {
  return address?.addressPrePlans?.[0] ?? null;
};
